interface EmbedSession {
  readonly sessionId: string;
  readonly embedUrl: string;
  readonly adminToken: string | null;
}

export interface RawEmbedSession {
  readonly sessionId: string;
  readonly embedUrl: string;
  readonly adminToken?: string | null;
}

interface CreateEmbedSessionInput {
  readonly startUrl: string;
  readonly width?: number;
  readonly height?: number;
}

interface EmbedSessionMutationResult<TData> {
  readonly data?: TData;
  readonly error?: { readonly message?: string } | null;
}

export interface EmbedSessionGateway {
  readonly create: (input: CreateEmbedSessionInput) => Promise<EmbedSession>;
  readonly remove: (sessionId: string) => Promise<void>;
}

export interface CreateEmbedSessionGatewayInput<TData> {
  readonly create: (
    input: CreateEmbedSessionInput
  ) => Promise<EmbedSessionMutationResult<TData>>;
  readonly remove: (sessionId: string) => Promise<unknown>;
  readonly select: (
    data: TData | undefined
  ) => RawEmbedSession | null | undefined;
  readonly toErrorMessage: (
    error: EmbedSessionMutationResult<TData>['error']
  ) => string;
  readonly noDataMessage: string;
  readonly removeFailureLabel: string;
}

export function createEmbedSessionGateway<TData>({
  create,
  remove,
  select,
  toErrorMessage,
  noDataMessage,
  removeFailureLabel,
}: CreateEmbedSessionGatewayInput<TData>): EmbedSessionGateway {
  return {
    async create(input): Promise<EmbedSession> {
      const result = await create(input);
      const session = select(result.data);

      if (result.error || !session) {
        throw new Error(toErrorMessage(result.error) || noDataMessage);
      }

      return {
        sessionId: session.sessionId,
        embedUrl: session.embedUrl,
        adminToken: session.adminToken ?? null,
      };
    },

    async remove(sessionId): Promise<void> {
      try {
        await remove(sessionId);
      } catch (error) {
        console.error(removeFailureLabel, error);
      }
    },
  };
}
